import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useHandleAppException, {
  AppExceptionResponseData,
} from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { getAuthLoginPath } from '../../../routes/auth.routes';
import {
  clearUrlHash,
  extractErrorCode,
  resolveSsoCallbackErrorLang,
} from '../../../services/modules/sso/sso.callbackError.utils';
import { useExchangeSsoHandoffMutation } from '../../../services/modules/sso/sso.mutations';
import { markFederatedSessionActive } from '../../../services/modules/sso/sso.utils';
import { useAuthContext } from '../../login/AuthContext';
import { useAuthLogin } from '../../login/hook/useAuthLogin';
import { useTranslation } from '../../translation/TranslationContext';
import {
  buildLocalException,
  extractHandoffCode,
  readAppExceptionData,
  resolveLoginBrandId,
} from './useSsoCallback.utils';

const useSsoCallback = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isMessagesLoaded } = useTranslation();
  const { handleError } = useHandleAppException();
  const { loginContext } = useAuthContext();
  const {
    doFederatedLogin,
    userInfo,
    openTeamModal,
    setOpenTeamModal,
    handleSelectTeam,
    openUsersModal,
    setOpenUsersModal,
    handleSelectPersonatedUser,
  } = useAuthLogin();
  const { mutateAsync: exchangeSsoHandoff } = useExchangeSsoHandoffMutation();

  const [exception, setException] = useState<AppExceptionResponseData | null>(null);
  const hasStartedExchangeRef = useRef(false);
  const hasReadHandoffCodeRef = useRef(false);
  const handoffCodeRef = useRef<string | null>(null);
  const errorCodeRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (hasReadHandoffCodeRef.current) {
      return;
    }
    hasReadHandoffCodeRef.current = true;

    handoffCodeRef.current = extractHandoffCode(window.location.hash);
    errorCodeRef.current = extractErrorCode(window.location.hash);
    clearUrlHash();
  }, []);

  useEffect(() => {
    if (!isMessagesLoaded || hasStartedExchangeRef.current) {
      return;
    }
    hasStartedExchangeRef.current = true;

    const callbackErrorLang = resolveSsoCallbackErrorLang(errorCodeRef.current);

    if (callbackErrorLang) {
      setException(buildLocalException(callbackErrorLang));
      return;
    }

    const handoffCode = handoffCodeRef.current;

    if (!handoffCode) {
      setException(buildLocalException(LangEnum.SSO_CALLBACK_LINK_INVALID));
      return;
    }

    const exchangeAndLogin = async () => {
      try {
        const loginBrandId = loginContext?.id ?? (await resolveLoginBrandId(slug));
        const session = await exchangeSsoHandoff(handoffCode);
        const result = await doFederatedLogin(session, loginBrandId);

        if (!result.ok) {
          setException(buildLocalException(LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN));
          return;
        }

        markFederatedSessionActive(slug);
      } catch (error) {
        setException(
          readAppExceptionData(error) ??
            buildLocalException(LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN),
        );
      }
    };

    exchangeAndLogin();
  }, [doFederatedLogin, exchangeSsoHandoff, isMessagesLoaded, loginContext?.id, slug]);

  const handleCancelSelection = () => {
    setOpenTeamModal(false);
    setOpenUsersModal(false);
    setException(buildLocalException(LangEnum.SSO_CALLBACK_SELECTION_CANCELLED));
  };

  return {
    errorMessage: exception ? handleError(exception) : null,
    brandLoginPath: getAuthLoginPath(slug),
    userInfo,
    openTeamModal,
    handleSelectTeam,
    openUsersModal,
    handleSelectPersonatedUser,
    handleCancelSelection,
  };
};

export default useSsoCallback;
